<template>
	<p class="dvd">{{ text }} {{ ' ' }}</p>
</template>

<script setup lang="ts">
import { MonetizationType, ObjectType, PresentationType } from '@/@types/graphql-types';
import { enquote } from '@/helpers/composables/useSpinningText';

import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed } from 'vue';
import {
	getDvdBlurRaySpinningTextPlaceHolder,
	DvdBluRayTextPlaceholderOfferKey,
	DvdBluRayTextPlaceholderStreamingState,
	sortedPresentationTypes,
	sortedMonetizationTypes,
} from './data/dvd-bluray';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';

type DvdBluRayPresentationType = PresentationType.Dvd | PresentationType.Bluray;
type OfferByMonetizationType = Record<DvdBluRayPresentationType, Record<string, string[]>>;
type TitleObjectType = ObjectType.Movie | ObjectType.Show | ObjectType.ShowSeason;

const props = defineProps<{
	title: string;
	titleObjectType: TitleObjectType;
	dvdBluRayOffers: TitleDetail['offers'];
	streamingOffers: TitleDetail['offers'];
	seasonNumber?: number | null;
}>();

const { t } = useI18n();

const getOfferByMonetizationTypeAndPresentationType = (offers: TitleDetail['offers']): OfferByMonetizationType => {
	return offers.reduce((acc, offer) => {
		const key = (
			offer.presentationType === PresentationType.Bluray_4K ? PresentationType.Bluray : offer.presentationType
		) as DvdBluRayPresentationType;

		if (!acc[key]) {
			acc[key] = {};
		}

		if (!acc[key][offer.monetizationType]) {
			acc[key][offer.monetizationType] = [];
		}

		if (!acc[key][offer.monetizationType].includes(offer.package.clearName)) {
			acc[key][offer.monetizationType].push(offer.package.clearName);
		}

		return acc;
	}, {} as OfferByMonetizationType);
};

function getFinaleText(
	title: string,
	hasStreamingOffers: boolean,
	dvdOffers: TitleDetail['offers'],
	blurayOffers: TitleDetail['offers']
) {
	const dvdOfferMap = getOfferByMonetizationTypeAndPresentationType([dvdOffers, blurayOffers].flat());

	const key: DvdBluRayTextPlaceholderOfferKey = (Object.keys(dvdOfferMap) as DvdBluRayPresentationType[])
		.sort((a, b) => sortedPresentationTypes.indexOf(a) - sortedPresentationTypes.indexOf(b))
		.map(presentationType => {
			const monetizationTypes = (Object.keys(dvdOfferMap[presentationType] ?? {}) as MonetizationType[]).sort(
				(a, b) => sortedMonetizationTypes.indexOf(a) - sortedMonetizationTypes.indexOf(b)
			);
			return `${presentationType}-${monetizationTypes.join('-')}`;
		})
		.join('-') as DvdBluRayTextPlaceholderOfferKey;

	const offersObj = {
		dvdOffers: Object.values(dvdOfferMap[PresentationType.Dvd] ?? {})
			.flat()
			.join(', '),
		blurayOffers: Object.values(dvdOfferMap[PresentationType.Bluray] ?? {})
			.flat()
			.join(', '),
		offers: Array.from(
			new Set(
				Object.values(dvdOfferMap)
					.flatMap(obj => Object.values(obj))
					.flat()
			)
		).join(', '),
	};

	const spinningTextPlaceHolder = getDvdBlurRaySpinningTextPlaceHolder(props.titleObjectType);
	const streamingKey: DvdBluRayTextPlaceholderStreamingState = hasStreamingOffers ? 'with-streaming' : 'no-streaming';

	const translatableKey = spinningTextPlaceHolder?.[streamingKey]?.[key];
	if (!translatableKey) {
		return '';
	}
	return t(translatableKey, {
		title,
		'dvd-offers': offersObj.dvdOffers,
		'blu-ray-offers': offersObj.blurayOffers,
		offers: offersObj.offers,
	});
}

const { locale } = storeToRefs(useLanguageStore());
const text = computed(() => {
	const displayedTitle =
		props.seasonNumber == null ? props.title : `${props.title} - ${t('WEBAPP_SEASON')} ${props.seasonNumber}`;
	return getFinaleText(
		enquote(displayedTitle, locale.value),
		props.streamingOffers.length > 0,
		props.dvdBluRayOffers.filter(offer => offer.presentationType === 'DVD'),
		props.dvdBluRayOffers.filter(
			offer => offer.presentationType === 'BLURAY' || offer.presentationType === 'BLURAY_4K'
		)
	);
});
</script>
